import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { catchError, switchMap } from 'rxjs/operators';
import { ApiService, CREDENTIAL_CHANGE_ROUTE, markPasswordExpired, vpnBlockDetails } from './api.service';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { SKIP_AUTH } from './client-ip.service';

const RENEW_PATH = '/v1/auth/renew';

const withToken = (req: HttpRequest<unknown>, token: string) =>
  req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

// Expired or revoked access token: renew once (shared with any other
// request doing the same) and retry this request once.
function retryOnUnauthorized(
  err: any,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  oApiService: ApiService,
): Observable<HttpEvent<unknown>> {
  if (!(err instanceof HttpErrorResponse) || err.status !== 401 || req.url.includes('logout')) {
    return throwError(() => err);
  }
  return oApiService.renewToken().pipe(
    catchError(() => throwError(() => err)),
    switchMap((newToken) => next(withToken(req, newToken))),
  );
}

// The session is valid but its password has expired: it may only change the
// password, so send the user there (once) and fail this request.
function redirectOnPasswordExpired(err: any, router: Router): boolean {
  if (!(err instanceof HttpErrorResponse) || err.status !== 403 || err.error?.code !== 'PASSWORD_EXPIRED') {
    return false;
  }
  markPasswordExpired();
  if (!router.url.startsWith(CREDENTIAL_CHANGE_ROUTE)) {
    router.navigate([CREDENTIAL_CHANGE_ROUTE]);
  }
  return true;
}

// auth-redis's VPN / proxy check logged this session out: show why (the
// VPN blocked page) and fail this request, without trying to renew.
function showOnVpnBlocked(err: any, oApiService: ApiService): boolean {
  const vpnBlock = vpnBlockDetails(err);
  if (!vpnBlock) {
    return false;
  }
  void oApiService.showVpnBlocked(vpnBlock);
  return true;
}

export const customInterceptor: HttpInterceptorFn = (req, next) => {
  const oApiService = inject(ApiService);
  const router = inject(Router);
  const oUserData = oApiService.getUserToken();
  if (oUserData == null || req.context.get(SKIP_AUTH)) {
    return next(req);
  }

  // The renew call is authorised by the refresh token; ApiService handles its failures.
  if (req.url.endsWith(RENEW_PATH)) {
    return next(withToken(req, oUserData.access_token));
  }

  // A 401 here means the refresh token was rejected and ApiService has logged
  // the user out, so stop. Any other failure falls back to the current token.
  const token$ = oApiService.isAboutToExpire()
    ? oApiService.renewToken().pipe(
        catchError((err) =>
          err?.status === 401 ? throwError(() => err) : of(oUserData.access_token),
        ),
      )
    : of(oUserData.access_token);

  return token$.pipe(
    switchMap((token) =>
      next(withToken(req, token)).pipe(
        catchError((err) =>
          showOnVpnBlocked(err, oApiService) || redirectOnPasswordExpired(err, router)
            ? throwError(() => err)
            : retryOnUnauthorized(err, req, next, oApiService),
        ),
      ),
    ),
  );
};
